import { Check } from 'lucide-react'
import type { ComponentProps, ReactNode, Ref } from 'react'

import Checkbox from '@/components/ui/Checkbox'
import { cn } from '@/lib/utils'

/**
 * `value` is narrowed by the caller so a page-size menu yields `10 | 20 | 50`
 * rather than `string`, and no menu in this family knows what a broker is.
 */
export interface MenuOption<T extends string = string> {
  value: T
  label: string
  /** Right-aligned figure — how many rows picking this option would keep. */
  count?: number | undefined
  /** Muted words after the label: the company's name beside its symbol. */
  detail?: string | undefined
  disabled?: boolean | undefined
  /**
   * Words a search also matches though the row does not print them: a fund's symbol behind a
   * row that shows only its name (MenuSingle's search).
   */
  keywords?: string | undefined
}

/**
 * 44px below `lg` via `min-h`, not padding: the row's vertical rhythm is set by
 * the checkbox and the label, and growing the padding to reach a touch target
 * pushes them apart instead of centring them in a taller box.
 *
 * Exported so a caller with a real row that isn't a `MenuItem` or
 * `MenuActionItem` (e.g. a `Link` acting as a menu row) can match these
 * metrics exactly instead of drifting from them.
 */
export const ROW =
  'flex min-h-11 w-full shrink-0 scroll-my-1 items-center rounded-sm px-3 py-2 text-left text-label-m transition-colors lg:min-h-9'

/**
 * A single-select row's look. `tick` (default) is the mock's `mkb-menu`: the current value on the
 * accent's soft tint in the link colour, with a tick that fades in. `plain` is its Movers sector
 * menu: secondary words, the current one in the primary at 600 with no tick, and a hover on the
 * tooltip's step-up fill. Both set the label at 14, as the mock's single-select menus do (the row
 * itself stays at the shared 12 for the multi-select and action rows).
 *
 * `tickLift` (sz:kit) is the Signal run menu (mock `sz-menu`): `tick`'s current row and tick, with
 * `plain`'s hover on the tooltip's step-up fill (#202020) instead of the inset grey.
 */
export type MenuItemSkin = 'tick' | 'tickLift' | 'plain'

const SINGLE_SKIN: Record<
  MenuItemSkin,
  { selected: string; idle: string; active: string; tick: boolean }
> = {
  tick: {
    // `accent-soft-foreground`, not `accent`: plain accent on the soft tint is 4.10:1 in light,
    // under AA for body text.
    selected: 'bg-tone-accent-soft text-label-l text-accent-soft-foreground',
    idle: 'text-label-l text-popover-foreground hover:bg-muted',
    active: 'bg-muted',
    tick: true,
  },
  tickLift: {
    selected: 'bg-tone-accent-soft text-label-l text-accent-soft-foreground',
    idle: 'text-label-l text-popover-foreground hover:bg-tooltip',
    active: 'bg-tooltip',
    tick: true,
  },
  plain: {
    selected: 'text-label-l font-semibold text-popover-foreground hover:bg-tooltip',
    idle: 'text-label-l text-muted-foreground hover:bg-tooltip hover:text-foreground',
    active: 'bg-tooltip text-foreground',
    tick: false,
  },
}

interface MenuItemBase {
  label: string
  detail?: string | undefined
  count?: number | undefined
  disabled?: boolean | undefined
  className?: string | undefined
  ref?: Ref<HTMLDivElement> | undefined
}

type MenuItemProps = MenuItemBase &
  (
    | {
        kind: 'single'
        /** `aria-activedescendant` names an id, not a node, so every option needs one. */
        id: string
        selected: boolean
        /** The roving highlight. Real focus stays on the field above the list. */
        active: boolean
        onSelect: () => void
        skin?: MenuItemSkin | undefined
      }
    | {
        /**
         * One row of a plain toggle list (the mock's `mkb-menu` company picker): the row itself is
         * the control, picked on the accent's soft tint with a tick, and each press commits.
         */
        kind: 'toggle'
        selected: boolean
        onToggle: (next: boolean) => void
      }
    | {
        kind: 'multi'
        checked: boolean | 'mixed'
        onToggle: (next: boolean) => void
        /** Trailing control on the Select-all row — the Clear action. */
        action?: ReactNode
        /** Only `option` rows join the arrow-key order; see MenuMulti. */
        row?: 'option' | 'all'
      }
  )

/**
 * One row of a menu, in the two shapes the design draws: a single-select
 * option that carries its own selected state, and a multi-select row whose
 * state lives in a Checkbox.
 *
 * The multi row is a plain element with a click handler and the Checkbox is
 * the only control in it — the Checkbox is what assistive tech announces and
 * what the keyboard operates. Its pointer events are switched off so that a
 * click anywhere on the row, the checkbox included, is handled exactly once by
 * the row; without that a click on the box itself would toggle twice.
 */
export default function MenuItem(props: MenuItemProps) {
  const { label, detail, count, disabled = false, className, ref } = props

  const figure =
    count === undefined ? null : (
      <span className="tnum shrink-0 text-body-xs text-muted-foreground">{count}</span>
    )

  if (props.kind === 'single') {
    const skin = SINGLE_SKIN[props.skin ?? 'tick']
    return (
      <div
        ref={ref}
        id={props.id}
        role="option"
        aria-selected={props.selected}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (!disabled) props.onSelect()
        }}
        className={cn(
          ROW,
          'cursor-pointer gap-2',
          props.selected ? skin.selected : skin.idle,
          props.active && !props.selected ? skin.active : null,
          disabled ? 'pointer-events-none opacity-50' : null,
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate" title={label}>
          {label}
        </span>
        {figure}
        {props.selected && skin.tick ? (
          <Check
            className="size-4 shrink-0 transition-opacity duration-med ease-out starting:opacity-0"
            aria-hidden="true"
          />
        ) : null}
      </div>
    )
  }

  if (props.kind === 'toggle') {
    const toggle = () => {
      if (!disabled) props.onToggle(!props.selected)
    }
    return (
      <div
        ref={ref}
        role="checkbox"
        aria-checked={props.selected}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        data-menu-row="option"
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault()
            toggle()
          }
        }}
        className={cn(
          ROW,
          'cursor-pointer gap-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
          props.selected
            ? 'bg-tone-accent-soft text-accent-soft-foreground'
            : 'text-popover-foreground hover:bg-muted',
          disabled ? 'pointer-events-none opacity-50' : null,
          className,
        )}
      >
        <span
          className="min-w-0 flex-1 truncate"
          title={detail === undefined ? label : `${label} · ${detail}`}
        >
          {label}
          {detail === undefined ? null : (
            <span className="ml-2 text-body-xs opacity-80">{detail}</span>
          )}
        </span>
        {props.selected ? (
          <Check
            className="size-4 shrink-0 transition-opacity duration-med ease-out starting:opacity-0"
            aria-hidden="true"
          />
        ) : null}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      data-menu-row={props.row ?? 'option'}
      onClick={() => {
        if (!disabled) props.onToggle(props.checked !== true)
      }}
      className={cn(
        ROW,
        'gap-2 text-popover-foreground',
        disabled ? 'opacity-50' : 'cursor-pointer hover:bg-muted has-[:focus-visible]:bg-muted',
        className,
      )}
    >
      <Checkbox
        checked={props.checked}
        onCheckedChange={props.onToggle}
        disabled={disabled}
        aria-label={count === undefined ? label : `${label}, ${String(count)}`}
        className="pointer-events-none shrink-0"
      />
      <span className="min-w-0 flex-1 truncate" title={label}>
        {label}
      </span>
      {figure}
      {props.action}
    </div>
  )
}

interface MenuActionItemProps extends Omit<ComponentProps<'button'>, 'children' | 'role' | 'type'> {
  label: string
  /** `text-destructive` for the row that removes something — Delete, Remove from list. */
  danger?: boolean | undefined
  /**
   * The hover fill. `inset` (default): the inset grey (#141414), as a row menu's items (mock
   * `wl-rowmenu`, Holdings' row ⋮). `lift` (rest:kit): the tooltip's step-up (#202020), as the
   * mock's raised menus without an edge (Portfolio switcher and options menus, `pfa-mi`) — the
   * action-menu twin of `MenuSingle skin="tickLift"`.
   */
  hover?: 'inset' | 'lift' | undefined
  /** A glyph before the label. Mark a + or × `data-turn` and it turns 90° on hover (`glyph-turn`). */
  icon?: ReactNode
}

/**
 * A menu row that DOES something — Edit, Delete, Add alert — rather than
 * picking a value: a `<button role="menuitem">` on the option rows' metrics, so
 * an action menu and a picker read as one family.
 *
 * Put it in a `role="menu"` container. Inside a `hasPopup="menu"` Popover, pass
 * `tabIndex={-1}`: the panel roves the items with the arrow keys. Closing the
 * menu is the caller's `onClick`, as it is for every Popover body.
 *
 * Unlike an option row it takes real focus, so it carries its own ring — inset,
 * as ProfileMenu's rows draw it: menu rows sit 4px apart, and an outward ring
 * would paint over its neighbours (and be clipped inside a Sheet's scrolling
 * body). Disabled rows keep their pointer events so a `title` naming the reason
 * still shows.
 */
export function MenuActionItem({
  label,
  danger = false,
  hover = 'inset',
  icon,
  className,
  ...props
}: MenuActionItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        ROW,
        'cursor-pointer gap-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50',
        hover === 'lift' ? 'enabled:hover:bg-tooltip' : 'enabled:hover:bg-muted',
        icon === undefined ? null : 'glyph-turn',
        danger ? 'text-destructive' : 'text-popover-foreground',
        className,
      )}
      {...props}
    >
      {icon}
      {/* The row's own `title` (a disabled row's reason) outranks the label's. */}
      <span
        className="min-w-0 flex-1 truncate"
        title={props.title === undefined ? label : undefined}
      >
        {label}
      </span>
    </button>
  )
}
